import { FC, useCallback, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { useLazyQuery } from "@apollo/client";
import { VENDOR_NAME_QUERY, VENDOR_ID_QUERY } from "@/apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import type { HgVendor } from "@/types/taxonomy";

interface VendorSingleSelectProps {
  selectedVendorId: number | null;
  onVendorSelect: (vendorId: number | null) => void;
  label?: string;
  inputTestId?: string;
}

interface VendorOption {
  id: number;
  name: string;
}

export const VendorSingleSelect: FC<VendorSingleSelectProps> = ({
  selectedVendorId,
  onVendorSelect,
  label = "Vendor",
  inputTestId,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<VendorOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<VendorOption | null>(
    null
  );

  const [searchVendors, { loading: searchLoading }] = useLazyQuery<{
    vendor: { data: HgVendor[] };
  }>(VENDOR_NAME_QUERY, { context: { clientName: TAXONOMY_CLIENT } });

  const [fetchVendorById] = useLazyQuery<{
    vendor: { data: HgVendor[] };
  }>(VENDOR_ID_QUERY, { context: { clientName: TAXONOMY_CLIENT } });

  // Hydrate selected vendor name on mount
  useEffect(() => {
    if (!selectedVendorId) {
      setSelectedOption(null);
      return;
    }
    if (selectedOption?.id === selectedVendorId) return;

    void fetchVendorById({ variables: { ids: [selectedVendorId] } }).then(
      ({ data }) => {
        const vendor = data?.vendor?.data?.[0];
        if (vendor) {
          setSelectedOption({ id: vendor.id, name: vendor.name });
        }
      }
    );
  }, [selectedVendorId, fetchVendorById, selectedOption?.id]);

  // Search vendors as user types
  const handleInputChange = useCallback(
    async (_: unknown, value: string) => {
      setInputValue(value);
      if (value.length < 2) {
        setOptions([]);
        return;
      }
      const { data } = await searchVendors({
        variables: { name: value },
      });
      const vendors = data?.vendor?.data ?? [];
      setOptions(vendors.map((v) => ({ id: v.id, name: v.name })));
    },
    [searchVendors]
  );

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={(_, option) => {
        setSelectedOption(option);
        onVendorSelect(option?.id ?? null);
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={searchLoading}
      filterOptions={(x) => x}
      renderInput={({ inputProps, ...params }) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search vendors..."
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchLoading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            htmlInput: {
              ...inputProps,
              ...(inputTestId ? { "data-testid": inputTestId } : {}),
            },
          }}
        />
      )}
    />
  );
};
